import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { FaCheckCircle, FaChevronDown, FaQuestionCircle, FaSearch } from 'react-icons/fa';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useNavigate, useLocation } from 'react-router-dom';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';

const AgencyCandidatePreference = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user?.id;
    const location = useLocation();
    const temporaryId = location.state?.temporaryId;
    console.log('receiving temporary_id:', temporaryId);

    // Refs for validation focus
    const locationPreferenceRef = useRef(null);
    const contractStatusRef = useRef(null);
    const genderRef = useRef(null);
    const countryPreferenceRef = useRef(null);
    const religionRef = useRef(null);
    const educationLevelRef = useRef(null);
    const ageRef = useRef(null);
    const experienceRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [ageType, setAgeType] = useState('range'); // 'range' or 'specific'
    const [specificAge, setSpecificAge] = useState('');
    const [formData, setFormData] = useState({
        location_preference: '',
        prefer_contract_status: '',
        gender: '',
        preferred_candidates_country: [],
        selected_country_for_contact: '',
        religion: '',
        education_level: '',
        age: '',
        preferred_years_of_experience: ''
    });

    const [showDropdown, setShowDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [additionalCountries] = useState([
        "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria",
        "Brazil", "Bulgaria", "Canada", "Chile", "Colombia", "Croatia", "Czech Republic",
        "Denmark", "Egypt", "Finland", "France", "Germany", "Greece", "Hungary",
        "Iceland", "Iran", "Iraq", "Ireland", "Israel", "Japan", "Jordan", "Kazakhstan",
        "Kuwait", "Lebanon", "Malaysia", "Mexico", "Morocco", "Netherlands", "New Zealand",
        "Nigeria", "Norway", "Oman", "Poland", "Portugal", "Qatar", "Romania", "Russia",
        "Saudi Arabia", "Serbia", "Singapore", "South Africa", "South Korea", "Spain",
        "Sweden", "Switzerland", "Syria", "Turkey", "UAE", "UK", "USA", "Venezuela",
        "Yemen", "Zambia", "Zimbabwe"
    ]);

    // Country to WhatsApp code mapping
    const countryWhatsAppMapping = {
        "Philippines": "+63",
        "India": "+91",
        "Nepal": "+977",
        "Srilanka": "+94",
        "Pakistan": "+92",
        "Ghana": "+233",
        "Kenya": "+254",
        "Uganda": "+256",
        "Burundi": "+257",
        "Indonesia": "+62",
        "Thailand": "+66",
        "Cambodia": "+855",
        "Tanzania": "+255",
        "Bangladesh": "+880",
        "Ethiopia": "+251",
        "Vietnam": "+84",
        "Ukraine": "+380",
        "Moldova": "+373",
        "Georgia": "+995",
        "Armenia": "+374",
        "Azerbaijan": "+994",
        "Kyrgyzstan": "+996",
        "Tajikistan": "+992",
        "Uzbekistan": "+998",
        "Turkmenistan": "+993",
        "Belarus": "+375",
        "China": "+86",
        "Any": "+971",
        "Bahrain": "+973",
        "Italy": "+39"
    };

    // Add effect to close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && !event.target.closest('.custom-dropdown-container')) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showDropdown]);

    // Function to parse field values
    const parseField = (fieldName, fieldValue) => {
        console.log(`Parsing ${fieldName}:`, fieldValue, typeof fieldValue);

        // Handle null/undefined/empty
        if (fieldValue === null || fieldValue === undefined || fieldValue === '' || fieldValue === 'null' || fieldValue === 'undefined') {
            if (fieldName === 'preferred_candidates_country') {
                return [];
            }
            return '';
        }

        // For array fields
        if (fieldName === 'preferred_candidates_country') {
            try {
                let valueToParse = fieldValue;

                // If it's already an array, return it
                if (Array.isArray(valueToParse)) {
                    return valueToParse;
                }

                // If it's a string, try to parse it as JSON
                if (typeof valueToParse === 'string') {
                    // Remove any extra quotes or brackets
                    valueToParse = valueToParse.trim();

                    // Handle empty JSON arrays
                    if (valueToParse === '[]' || valueToParse === '""' || valueToParse === '"[]"') {
                        return [];
                    }

                    // Try to parse as JSON
                    try {
                        const parsed = JSON.parse(valueToParse);
                        if (Array.isArray(parsed)) {
                            return parsed;
                        }
                        // If parsing returns a string, try parsing again
                        if (typeof parsed === 'string') {
                            try {
                                const secondParse = JSON.parse(parsed);
                                if (Array.isArray(secondParse)) {
                                    return secondParse;
                                }
                                if (typeof secondParse === 'string') {
                                    const thirdParse = JSON.parse(secondParse);
                                    if (Array.isArray(thirdParse)) {
                                        return thirdParse;
                                    }
                                }
                            } catch (e) {
                                // If it's a string that's not JSON, check if it looks like an array
                                if (parsed.startsWith('[') && parsed.endsWith(']')) {
                                    return [parsed];
                                }
                                return [];
                            }
                        }
                    } catch (parseError) {
                        console.log(`Failed to parse ${fieldName} as JSON:`, fieldValue);
                        // If it looks like a simple string value (not JSON), wrap it in array
                        if (typeof valueToParse === 'string' && valueToParse.length > 0) {
                            return [valueToParse];
                        }
                        return [];
                    }
                }

                return [];
            } catch (e) {
                console.error(`Error parsing ${fieldName}:`, e);
                return [];
            }
        }

        // For string fields (including preferred_years_of_experience)
        try {
            let stringValue = fieldValue;

            // If it's already a string, use it
            if (typeof stringValue === 'string') {
                // Remove any JSON encoding
                stringValue = stringValue.trim();

                // Remove quotes if present
                if ((stringValue.startsWith('"') && stringValue.endsWith('"')) ||
                    (stringValue.startsWith("'") && stringValue.endsWith("'"))) {
                    stringValue = stringValue.slice(1, -1);
                }

                // Try to parse as JSON in case it's encoded
                if (stringValue.startsWith('"') || stringValue.startsWith('[') || stringValue.startsWith('{')) {
                    try {
                        const parsed = JSON.parse(stringValue);
                        if (typeof parsed === 'string') {
                            return parsed;
                        }
                        // If it's an array with one item, return that item
                        if (Array.isArray(parsed) && parsed.length === 1) {
                            return parsed[0].toString();
                        }
                    } catch (jsonError) {
                        // Not valid JSON, return as is
                    }
                }

                return stringValue;
            }

            // For non-strings, convert to string
            return stringValue.toString();
        } catch (e) {
            console.error(`Error converting ${fieldName} to string:`, e);
            return '';
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Function to determine age type and values from stored data
    const initializeAgeData = (ageValue) => {
        if (!ageValue) {
            setAgeType('range');
            setSpecificAge('');
            return;
        }

        // Check if it's a range (contains hyphen) or specific age
        if (ageValue.includes('-') || ageValue.includes('above')) {
            setAgeType('range');
        } else {
            setAgeType('specific');
            setSpecificAge(ageValue);
        }
    };

    // Fetch existing data
    const fetchSavedData = async () => {
        try {
            const response = await fetch(`${BaseUrl}/api/employer?temporary_id=${temporaryId}&user_id=${userId}`);
            if (response.ok) {
                const data = await response.json();

                if (data) {
                    // Log the data to see what's coming from API
                    console.log('Data from API:', data);
                    console.log('preferred_years_of_experience raw:', data.preferred_years_of_experience);

                    setFormData({
                        location_preference: parseField('location_preference', data.location_preference),
                        prefer_contract_status: parseField('prefer_contract_status', data.prefer_contract_status),
                        gender: parseField('gender', data.gender),
                        preferred_candidates_country: parseField('preferred_candidates_country', data.preferred_candidates_country),
                        selected_country_for_contact: parseField('selected_country_for_contact', data.selected_country_for_contact),
                        religion: parseField('religion', data.religion),
                        education_level: parseField('education_level', data.education_level),
                        age: parseField('age', data.age),
                        preferred_years_of_experience: parseField('preferred_years_of_experience', data.preferred_years_of_experience)
                    });

                    // Initialize age data based on stored value
                    initializeAgeData(data.age || '');
                    setIsSaved(true);

                    // Log the parsed formData
                    console.log('Parsed formData:', {
                        location_preference: parseField('location_preference', data.location_preference),
                        prefer_contract_status: parseField('prefer_contract_status', data.prefer_contract_status),
                        gender: parseField('gender', data.gender),
                        preferred_candidates_country: parseField('preferred_candidates_country', data.preferred_candidates_country),
                        selected_country_for_contact: parseField('selected_country_for_contact', data.selected_country_for_contact),
                        religion: parseField('religion', data.religion),
                        education_level: parseField('education_level', data.education_level),
                        age: parseField('age', data.age),
                        preferred_years_of_experience: parseField('preferred_years_of_experience', data.preferred_years_of_experience)
                    });
                }
            } else {
                console.error('Failed to fetch data:', response.status);
            }
        } catch (error) {
            console.error('Error fetching employer data:', error);
            Swal.fire('Error', 'Failed to fetch employer data', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchSavedData();
        }
    }, [userId, temporaryId]);

    const handleSelect = (field, value) => {
        // For fields that can only have one value
        if (['location_preference', 'prefer_contract_status', 'gender', 'religion', 'education_level', 'age', 'selected_country_for_contact', 'preferred_years_of_experience'].includes(field)) {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        // For array fields
        else if (['preferred_candidates_country'].includes(field)) {
            setFormData(prev => {
                const currentArray = Array.isArray(prev[field]) ? prev[field] : [];
                if (currentArray.includes(value)) {
                    return {
                        ...prev,
                        [field]: currentArray.filter(item => item !== value)
                    };
                } else {
                    return {
                        ...prev,
                        [field]: [...currentArray, value]
                    };
                }
            });
        }
        setIsSaved(false);
    };

    const handleAgeTypeChange = (type) => {
        setAgeType(type);
        // Clear age field when switching types
        setFormData(prev => ({
            ...prev,
            age: ''
        }));
        setSpecificAge('');
        setIsSaved(false);
    };

    const handleSpecificAgeChange = (age) => {
        setSpecificAge(age);
        // Update the formData age field with specific age
        setFormData(prev => ({
            ...prev,
            age: age
        }));
        setIsSaved(false);
    };

    const handleAgeRangeSelect = (ageRange) => {
        // Update the formData age field with selected range
        setFormData(prev => ({
            ...prev,
            age: ageRange
        }));
        setIsSaved(false);
    };

    const isSelected = (field, value) => {
        if (Array.isArray(formData[field])) {
            return formData[field].includes(value);
        }
        return formData[field] === value;
    };

    // Focus function for validation errors
    const focusOnField = (fieldName) => {
        setTimeout(() => {
            let element = null;

            switch (fieldName) {
                case 'gender':
                    element = genderRef.current;
                    break;
                default:
                    break;
            }

            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });

                setTimeout(() => {
                    if (element.focus) {
                        element.focus();
                        if (document.activeElement !== element) {
                            element.focus();
                        }
                    }
                }, 300);
            }
        }, 100);
    };

    // Validation function
    const validateFormData = async () => {
        const requiredFields = [
            { field: 'gender', name: 'Gender' },
        ];

        const missingFields = requiredFields.filter(item => {
            if (item.field === 'preferred_candidates_country') {
                return !formData[item.field] || formData[item.field].length === 0;
            }
            if (item.field === 'preferred_years_of_experience') {
                return !formData[item.field] || formData[item.field].trim() === '';
            }
            return !formData[item.field];
        });

        if (missingFields.length > 0) {
            const result = await Swal.fire({
                title: 'Error!',
                text: `Please fill in all required fields: ${missingFields.map(item => item.name).join(', ')}`,
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#d33',
            });

            if (result.isConfirmed || result.isDismissed) {
                const firstMissingField = missingFields[0];
                focusOnField(firstMissingField.field);
            }
            return false;
        }

        // Validate specific age input
        if (ageType === 'specific' && specificAge) {
            const ageNum = parseInt(specificAge);
            if (ageNum < 18 || ageNum > 100) {
                const result = await Swal.fire({
                    title: 'Error!',
                    text: 'Please enter a valid age between 18 and 100',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#d33',
                });

                if (result.isConfirmed || result.isDismissed) {
                    focusOnField('age');
                }
                return false;
            }
        }

        return true;
    };

    // Save data function
    const saveData = async () => {
        setIsLoading(true);

        Swal.fire({
            title: 'Saving...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            // Prepare the payload
            const payload = {
                temporary_id: temporaryId,
                user_id: userId,
                location_preference: formData.location_preference,
                prefer_contract_status: formData.prefer_contract_status,
                gender: formData.gender,
                preferred_candidates_country: JSON.stringify(formData.preferred_candidates_country),
                selected_country_for_contact: formData.selected_country_for_contact,
                religion: formData.religion,
                education_level: formData.education_level,
                age: formData.age,
                preferred_years_of_experience: formData.preferred_years_of_experience
            };

            const response = await fetch(`${BaseUrl}/api/employer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to save data');
            }

            await Swal.fire({
                title: 'Success!',
                text: 'Your candidate preferences have been saved successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3085d6',
            });

            setIsSaved(true);
            return data;
        } catch (err) {
            console.error('Error saving data:', err);

            await Swal.fire({
                title: 'Error!',
                text: err.message || 'Failed to save data. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#d33',
            });

            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        const isValid = await validateFormData();
        if (!isValid) return;

        try {
            await saveData();
        } catch (err) {
            // Error already handled in saveData
        }
    };

    const handleNext = async () => {
        const isValid = await validateFormData();
        if (!isValid) return;

        try {
            await saveData();
            window.scrollTo(0, 0);
            navigate('/a-aboutyourself', { state: { userId, temporaryId } });
        } catch (err) {
            // Error already handled in saveData
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <AgencyNavbar />
            <Container fluid className="job-form py-4" style={{ marginTop: '70px' }}>
                <div className="form-header text-center">
                    <h1 className="candidate-pref-heading d-flex align-items-center justify-content-center">
                        <i className="bi bi-person-vcard me-2"></i>
                        Candidate Preference
                    </h1>
                </div>

                {/* Location Preference */}
                <div className="section mt-3" ref={locationPreferenceRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black' }}>Country/Location Preference</span>
                    </h5>
                    <Row className="g-3 mb-3">
                        {["Any Country", "Inside the country", "Out side the Country"].map((option, idx) => (
                            <Col xs={4} md={4} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('location_preference', option) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('location_preference', option)}
                                >
                                    {option}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Contract Status */}
                <div className="section mt-3" ref={contractStatusRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black' }}>Prefer Contract Status</span>
                    </h5>
                    <Row className="g-3 mb-3">
                        {["Any", "Finished Contract", "Terminated", "Break contract",
                            "Transferable", "Any overseas", "Resigned", "First timer"].map((status, idx) => (
                                <Col xs={6} md={3} key={idx}>
                                    <Button
                                        className={`language-btn w-100 ${isSelected('prefer_contract_status', status) ? 'selected-button' : ''}`}
                                        variant="outline-primary"
                                        onClick={() => handleSelect('prefer_contract_status', status)}
                                    >
                                        {status}
                                    </Button>
                                </Col>
                            ))}
                    </Row>
                </div>

                {/* Gender */}
                <div className="section mt-3" ref={genderRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black' }}>Gender<span className="text-danger">*</span></span>
                    </h5>
                    <Row className="g-3 mb-3">
                        {["Any", "Male", "Female"].map((gender, idx) => (
                            <Col xs={4} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('gender', gender) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('gender', gender)}
                                >
                                    {gender}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Religion */}
                <div className="section mt-3" ref={religionRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black' }}>Religion</span>
                    </h5>
                    <Row className="g-3 mb-3">
                        {["Muslim", "Hindu", "Christian", "Any"].map((religion, idx) => (
                            <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('religion', religion) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('religion', religion)}
                                >
                                    {religion}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Education Level */}
                <div className="section mt-3" ref={educationLevelRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black' }}>Education Level</span>
                    </h5>
                    <Row className="g-3 mb-3">
                        {["Below 10th", "High school", "12th", "Bachelor", "Master", "Doctorate", "Diploma Nurse", "BSC Nurse", "MSC Nurse", "Diploma in Physiotherapy", "Bachelor (BPT)", "Master (MPT)", "Certified Nurse", "University"].map((education, idx) => (
                            <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('education_level', education) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('education_level', education)}
                                >
                                    {education}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Age */}
                <div className="section mt-3" ref={ageRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black', marginBottom: '-10px' }}>Age</span>
                    </h5>

                    {/* Age Type Selection */}
                    <Row className="g-3 mb-1">
                        <Col xs={12}>
                            <div className="mb-1">
                                <Form.Select
                                    value={ageType}
                                    onChange={(e) => handleAgeTypeChange(e.target.value)}
                                    className="dropdown-btn w-100"
                                >
                                    <option value="range">Age Range</option>
                                    <option value="specific">Specific Age</option>
                                </Form.Select>
                            </div>
                        </Col>
                    </Row>

                    {ageType === 'range' && (
                        <Row className="g-3 mb-3">
                            {["18-20", "20-25", "25-30", "30-35", "35-40", "40-45", "45-50", "Below 30", "Below 35", "21-40", "Below 40", "Below 45", "Below 50", "Below 55"].map((age, idx) => (
                                <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                                    <Button
                                        className={`language-btn w-100 ${isSelected('age', age) ? 'selected-button' : ''}`}
                                        variant="outline-primary"
                                        onClick={() => handleAgeRangeSelect(age)}
                                    >
                                        {age}
                                    </Button>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {ageType === 'specific' && (
                        <Row className="g-3 mb-3">
                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold" style={{ fontSize: '20px' }}>
                                        Enter Specific Age
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="18"
                                        max="100"
                                        value={specificAge}
                                        onChange={(e) => handleSpecificAgeChange(e.target.value)}
                                        placeholder="Enter age (e.g., 25)"
                                        className="dropdown-btn w-100"
                                        style={{ height: '65px', fontSize: '20px', color: 'black' }}
                                    />
                                    <Form.Text className="text-muted">
                                        Please enter age between 18 and 100
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </div>

                {/* Prefer years of experience */}
                <div className="section mt-3" ref={experienceRef}>
                    <h5 className="d-flex align-items-center candidate-preference">
                        <span style={{ color: 'black' }}>Prefer years of experience</span>
                    </h5>
                    <Row className="g-3 mb-3">
                        {["Fresher", "1-2 year", "2-4 year", "4-6 year", "6-8 year", "8-10 year", "10-12 year", "12 Above"].map((experience, idx) => (
                            <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('preferred_years_of_experience', experience) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('preferred_years_of_experience', experience)}
                                >
                                    {experience}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Navigation Buttons */}
                <div className="additional-section mb-4" style={{ marginTop: '20px' }}>
                    <div
                        className="d-flex justify-content-between gap-2"
                        style={{ width: "100%" }}
                    >
                        <Button
                            className="nav-btn"
                            style={{ flex: 1 }}
                            variant="secondary"
                            onClick={() => navigate('/a-requiredskills', { state: { userId, temporaryId } })}
                        >
                            Back
                        </Button>

                        <Button
                            className="nav-btn"
                            style={{ flex: 1 }}
                            variant="primary"
                            onClick={handleNext}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Next'}
                        </Button>
                    </div>
                </div>
            </Container>
        </>
    );
};

export default AgencyCandidatePreference;
